import { CSRF_TOKEN_STORAGE, USER_STORAGE } from "../constants";

const MAX_STRING_LENGTH = 2000;
const DEDUPLICATION_WINDOW_MS = 10000;
const recentReports = new Map();

const truncate = (value, maxLength = MAX_STRING_LENGTH) => {
  if (value === null || value === undefined) return null;
  const stringValue = String(value);
  return stringValue.length > maxLength
    ? `${stringValue.slice(0, maxLength)}...`
    : stringValue;
};

const getStoredUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem(USER_STORAGE)) || {};
  } catch {
    return {};
  }
};

export const createRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getReportSignature = (report) =>
  [report.source, report.name, report.message, report.api?.method, report.api?.url]
    .filter(Boolean)
    .join("|");

const shouldReport = (report) => {
  const signature = getReportSignature(report);
  const now = Date.now();
  const lastReportAt = recentReports.get(signature);
  recentReports.set(signature, now);

  for (const [key, createdAt] of recentReports.entries()) {
    if (now - createdAt > DEDUPLICATION_WINDOW_MS) recentReports.delete(key);
  }

  return !lastReportAt || now - lastReportAt > DEDUPLICATION_WINDOW_MS;
};

export const buildClientErrorReport = (error, context = {}) => {
  const response = error?.response;
  const config = error?.config;
  const requestId =
    response?.headers?.["x-request-id"] ||
    config?.headers?.["x-request-id"] ||
    context.requestId ||
    null;

  return {
    source: context.source || "frontend",
    name: truncate(error?.name || "Error", 120),
    message: truncate(
      response?.data?.message ||
        response?.data?.error ||
        error?.message ||
        "Erreur frontend inconnue",
    ),
    stack: truncate(error?.stack, 8000),
    componentStack: truncate(context.componentStack, 8000),
    route: truncate(window.location.pathname, 500),
    api: config
      ? {
          method: truncate(config.method?.toUpperCase(), 12),
          url: truncate(config.url?.split("?")[0], 1000),
          status: response?.status || null,
          code: truncate(error?.code, 100),
          durationMs: config.metadata?.startedAt
            ? Date.now() - config.metadata.startedAt
            : null,
        }
      : null,
    requestId,
    userAgent: truncate(window.navigator?.userAgent, 500),
    occurredAt: new Date().toISOString(),
    ...context.details,
  };
};

export const reportClientError = async (error, context = {}) => {
  const report = buildClientErrorReport(error, context);
  if (!shouldReport(report)) return;

  const storedUser = getStoredUser();
  const token = storedUser?.token;
  if (!token || !process.env.REACT_APP_API_URL) return;

  const csrfToken = sessionStorage.getItem(CSRF_TOKEN_STORAGE);
  const endpoint = `${process.env.REACT_APP_API_URL.replace(/\/?$/, "/")}logs/system/client`;

  try {
    await fetch(endpoint, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        "x-access-token": token,
        ...(csrfToken ? { "x-csrf-token": csrfToken } : {}),
        ...(report.requestId ? { "x-request-id": report.requestId } : {}),
      },
      body: JSON.stringify(report),
    });
  } catch {
    // La journalisation ne doit jamais interrompre le parcours utilisateur.
  }
};
